import { useEffect, useRef, useState } from 'react';
import type { ConnectionView, ServerEvent } from '@floorpoll/shared';
import { config } from '../config.js';

export type LiveStatus = 'connecting' | 'live' | 'reconnecting';

interface Options {
  /**
   * How to address the quiz. Takers use their join code because they are
   * never told the quizId — it is what unlocks the big-screen results feed.
   */
  target: { quizId: string } | { code: string } | null;
  view: ConnectionView;
  /** Applied to local state as each push arrives. */
  onEvent: (event: ServerEvent) => void;
  /**
   * Called on first connect and after every reconnect. This is the only place
   * a full state fetch happens — everything else arrives as a push (spec §6).
   */
  onResync: () => void;
}

const MAX_BACKOFF_MS = 10_000;

/**
 * Holds a WebSocket open for the duration of a presentation.
 *
 * There is deliberately no polling anywhere: votes and screen changes arrive
 * as server pushes. The only repeated timer is reconnection backoff, which
 * runs only while the socket is actually down.
 */
export function useLiveQuiz({ target, view, onEvent, onResync }: Options): LiveStatus {
  const [status, setStatus] = useState<LiveStatus>('connecting');

  // Kept in refs so a changing callback identity never tears down the socket.
  const onEventRef = useRef(onEvent);
  const onResyncRef = useRef(onResync);
  onEventRef.current = onEvent;
  onResyncRef.current = onResync;

  // A primitive keeps the effect from re-running on every render just because
  // the caller built a fresh object.
  const targetKey = target
    ? 'quizId' in target
      ? `quizId=${encodeURIComponent(target.quizId)}`
      : `code=${encodeURIComponent(target.code)}`
    : null;

  useEffect(() => {
    if (!targetKey) return;

    let socket: WebSocket | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let closed = false;

    const connect = () => {
      if (closed) return;

      socket = new WebSocket(`${config.wsBaseUrl}?${targetKey}&view=${view}`);

      socket.onopen = () => {
        attempt = 0;
        setStatus('live');
        // Recover anything missed while the socket was down. Missed events are
        // never replayed; we re-read current state instead.
        onResyncRef.current();
      };

      socket.onmessage = (message) => {
        try {
          onEventRef.current(JSON.parse(message.data as string) as ServerEvent);
        } catch {
          // A malformed frame must not kill the presentation.
        }
      };

      socket.onclose = () => {
        if (closed) return;
        setStatus('reconnecting');
        const delay = Math.min(500 * 2 ** attempt, MAX_BACKOFF_MS);
        attempt += 1;
        retryTimer = setTimeout(connect, delay);
      };

      socket.onerror = () => socket?.close();
    };

    connect();

    return () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      socket?.close();
    };
  }, [targetKey, view]);

  return status;
}
